import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, Shield, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

interface WidgetToken {
  id: string;
  token: string;
  domain_whitelist: string[] | null;
  is_active: boolean;
  created_at: string;
}

interface DomainManagerProps {
  tokens: WidgetToken[];
  onTokensUpdate: (tokens: WidgetToken[]) => void;
}

export const DomainManager: React.FC<DomainManagerProps> = ({
  tokens,
  onTokensUpdate,
}) => {
  const [selectedTokenId, setSelectedTokenId] = useState<string>(
    tokens.length > 0 ? tokens[0].id : ''
  );
  const [newDomain, setNewDomain] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const selectedToken = tokens.find(t => t.id === selectedTokenId);

  const addDomain = async () => {
    if (!selectedToken || !newDomain.trim()) return;

    const domain = newDomain.trim().toLowerCase();
    
    // Basic domain validation
    if (!/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(domain)) {
      toast.error('Please enter a valid domain name');
      return;
    }

    const currentDomains = selectedToken.domain_whitelist || [];
    if (currentDomains.includes(domain)) {
      toast.error('Domain already exists in whitelist');
      return;
    }

    setIsUpdating(true);
    try {
      const updatedDomains = [...currentDomains, domain];
      
      const { data, error } = await supabase
        .from('hive360_tokens')
        .update({ domain_whitelist: updatedDomains })
        .eq('id', selectedToken.id)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      const updatedTokens = tokens.map(token =>
        token.id === selectedToken.id
          ? { ...token, domain_whitelist: updatedDomains }
          : token
      );
      onTokensUpdate(updatedTokens);
      
      setNewDomain('');
      toast.success('Domain added to whitelist');
    } catch (error) {
      console.error('Error adding domain:', error);
      toast.error('Failed to add domain');
    } finally {
      setIsUpdating(false);
    }
  };

  const removeDomain = async (domainToRemove: string) => {
    if (!selectedToken) return;

    setIsUpdating(true);
    try {
      const currentDomains = selectedToken.domain_whitelist || [];
      const updatedDomains = currentDomains.filter(d => d !== domainToRemove);
      
      const { data, error } = await supabase
        .from('hive360_tokens')
        .update({ domain_whitelist: updatedDomains.length > 0 ? updatedDomains : null })
        .eq('id', selectedToken.id)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      const updatedTokens = tokens.map(token =>
        token.id === selectedToken.id
          ? { ...token, domain_whitelist: updatedDomains.length > 0 ? updatedDomains : null }
          : token
      );
      onTokensUpdate(updatedTokens);
      
      toast.success('Domain removed from whitelist');
    } catch (error) {
      console.error('Error removing domain:', error);
      toast.error('Failed to remove domain');
    } finally {
      setIsUpdating(false);
    }
  };

  const clearAllDomains = async () => {
    if (!selectedToken) return;

    setIsUpdating(true);
    try {
      const { data, error } = await supabase
        .from('hive360_tokens')
        .update({ domain_whitelist: null })
        .eq('id', selectedToken.id)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      const updatedTokens = tokens.map(token =>
        token.id === selectedToken.id
          ? { ...token, domain_whitelist: null }
          : token
      );
      onTokensUpdate(updatedTokens);
      
      toast.success('All domains removed - widget can now embed on any domain');
    } catch (error) {
      console.error('Error clearing domains:', error);
      toast.error('Failed to clear domains');
    } finally {
      setIsUpdating(false);
    }
  };

  if (tokens.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Domain Management
          </CardTitle>
          <CardDescription>
            Generate a token first to manage domain restrictions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              No tokens available for domain management
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Domain Management
        </CardTitle>
        <CardDescription>
          Control which domains can embed your widget for enhanced security
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Token Selection */}
        <div className="space-y-2">
          <Label>Select Token</Label>
          <Select value={selectedTokenId} onValueChange={setSelectedTokenId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a token to manage" />
            </SelectTrigger>
            <SelectContent>
              {tokens.map((token) => (
                <SelectItem key={token.id} value={token.id}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">
                      {token.token.substring(0, 20)}...
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {token.domain_whitelist?.length ? 
                        `${token.domain_whitelist.length} domain${token.domain_whitelist.length !== 1 ? 's' : ''}` : 
                        'Any domain'
                      }
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedToken && (
          <>
            {/* Current Domain Status */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="h-4 w-4" />
                <span className="font-medium">Current Status</span>
              </div>
              {!selectedToken.domain_whitelist || selectedToken.domain_whitelist.length === 0 ? (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                    Any Domain
                  </Badge>
                  <span className="text-sm text-gray-600">
                    Widget can be embedded on any website
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    Restricted
                  </Badge>
                  <span className="text-sm text-gray-600">
                    Widget restricted to {selectedToken.domain_whitelist.length} domain(s)
                  </span>
                </div>
              )}
            </div>

            {/* Add New Domain */}
            <div className="space-y-2">
              <Label htmlFor="newDomain">Add Domain to Whitelist</Label>
              <div className="flex gap-2">
                <Input
                  id="newDomain"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="example.com"
                  onKeyPress={(e) => e.key === 'Enter' && addDomain()}
                />
                <Button 
                  onClick={addDomain} 
                  disabled={!newDomain.trim() || isUpdating}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Enter domain without protocol (e.g., "example.com", not "https://example.com")
              </p>
            </div>

            {/* Current Domains */}
            {selectedToken.domain_whitelist && selectedToken.domain_whitelist.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Allowed Domains</Label>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={clearAllDomains}
                    disabled={isUpdating}
                  >
                    Allow All Domains
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {selectedToken.domain_whitelist.map((domain) => (
                    <div key={domain} className="flex items-center justify-between bg-white border rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-gray-400" />
                        <span className="font-mono text-sm">{domain}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDomain(domain)}
                        disabled={isUpdating}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Security Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Security Recommendations</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Restrict domains for production widgets to prevent unauthorized use</li>
                <li>• Use subdomain wildcards with caution (not currently supported)</li>
                <li>• Regularly review and update your domain whitelist</li>
                <li>• For testing, you can temporarily allow all domains</li>
              </ul>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};